"use client";

import * as React from "react";

import { cn } from "lib/utils";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { Button } from "js/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "js/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";

/**
 * Normalize strings for accent- and case-insensitive comparisons.
 * SEPOMEX returns labels with diacritics (e.g. "Álamos") but consumers
 * may store the selected value normalized (uppercased + stripped of
 * diacritics), so every comparison inside the combobox needs to run
 * through this helper or it will miss legitimate matches.
 */
const normalize = (str: string): string =>
  str
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export interface ColoniaOption {
  value: string;
  label: string;
}

export interface ColoniaComboboxProps {
  /**
   * Current value (colonia name)
   */
  value: string;
  /**
   * Callback when value changes
   */
  onChange: (value: string) => void;
  /**
   * Available colonia options from SEPOMEX
   */
  options: ColoniaOption[];
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the combobox is disabled
   */
  disabled?: boolean;
  /**
   * Whether data is currently loading
   */
  isLoading?: boolean;
  /**
   * Allow entering custom values not in the list
   */
  allowCustomValue?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Callback when input value changes (for filtering)
   */
  onInputChange?: (value: string) => void;
  onBlur?: () => void;
}

export function ColoniaCombobox({
  value,
  onChange,
  options,
  placeholder = "Selecciona colonia",
  disabled = false,
  isLoading = false,
  allowCustomValue = true,
  error,
  className,
  onInputChange,
  onBlur,
}: ColoniaComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  // Update input value when external value changes
  React.useEffect(() => {
    if (!open) {
      setInputValue(value);
    }
  }, [value, open]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onInputChange?.(newValue);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setInputValue(selectedValue);
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen && allowCustomValue && inputValue && inputValue !== value) {
      // When closing, if there's a custom value typed, use it
      onChange(inputValue);
    }

    if (!newOpen) {
      onBlur?.();
    }
  };

  const handleTriggerBlur = () => {
    if (!open) {
      onBlur?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && allowCustomValue && inputValue) {
      e.preventDefault();
      // Check if input matches any option (accent- and case-insensitive)
      const normalizedInput = normalize(inputValue);
      const matchingOption = options.find(
        (opt) => normalize(opt.label) === normalizedInput,
      );
      if (matchingOption) {
        handleSelect(matchingOption.value);
      } else {
        // Use custom value
        onChange(inputValue);
        setOpen(false);
      }
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Filter options based on input (accent- and case-insensitive)
  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return options;
    const normalizedSearch = normalize(inputValue);
    return options.filter((opt) =>
      normalize(opt.label).includes(normalizedSearch),
    );
  }, [options, inputValue]);

  const showCustomValueOption =
    allowCustomValue &&
    inputValue &&
    !options.some((opt) => normalize(opt.label) === normalize(inputValue));

  const displayValue = value || placeholder;
  const hasOptions = options.length > 0;
  const hasFilteredOptions = filteredOptions.length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Seleccionar colonia"
          disabled={disabled}
          onBlur={handleTriggerBlur}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            error && "border-red-500",
            className,
          )}
        >
          <span className="truncate">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                Cargando colonias...
              </span>
            ) : (
              displayValue
            )}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar o escribir colonia..."
            value={inputValue}
            onValueChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete="address-level3"
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" />
                Cargando colonias...
              </div>
            ) : (
              <>
                {!hasFilteredOptions && !showCustomValueOption && (
                  <CommandEmpty>
                    {hasOptions && !allowCustomValue
                      ? "No se encontraron colonias"
                      : allowCustomValue
                        ? "Escribe el nombre de la colonia"
                        : "No se encontraron colonias"}
                  </CommandEmpty>
                )}
                {(hasFilteredOptions || showCustomValueOption) && (
                  <CommandGroup>
                    {showCustomValueOption && (
                      <CommandItem
                        value={`custom-${inputValue}`}
                        onSelect={() => handleSelect(inputValue)}
                        className="text-muted-foreground"
                      >
                        <Check
                          aria-hidden
                          className={cn(
                            "mr-2 size-4",
                            value === inputValue ? "opacity-100" : "opacity-0",
                          )}
                        />
                        Usar: "{inputValue}"
                      </CommandItem>
                    )}
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <Check
                          aria-hidden
                          className={cn(
                            "mr-2 size-4",
                            normalize(value) === normalize(option.value)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
